import React, { Component } from "react";
import { FaCircle, FaRedo, FaStop, FaVideo } from "react-icons/fa";
import { Translate, I18n } from "react-redux-i18n";
import alertify from "alertifyjs";

const MAX_RECORD_SECONDS = 15;

class UserVerificationFaceRecorder extends Component {
  videoRef = React.createRef();
  mediaRecorder = null;
  stream = null;
  chunks = [];
  timerId = null;

  state = {
    recording: false,
    seconds: 0,
    previewUrl: "",
    starting: false,
  };

  componentDidMount() {
    if (this.props.active && !this.props.disabled) {
      this.startCamera();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.active && !prevProps.active && !this.props.disabled) {
      this.startCamera();
    }

    if ((!this.props.active && prevProps.active) || (this.props.disabled && !prevProps.disabled)) {
      this.resetRecorder();
    }
  }

  componentWillUnmount() {
    this.resetRecorder();
  }

  stopStream = () => {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }

    if (this.videoRef.current) {
      this.videoRef.current.srcObject = null;
    }
  };

  clearTimer = () => {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  };

  resetRecorder = () => {
    this.clearTimer();

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }

    this.mediaRecorder = null;
    this.chunks = [];
    this.stopStream();

    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }

    this.setState({
      recording: false,
      seconds: 0,
      previewUrl: "",
      starting: false,
    });
    this.props.onVideoChange(null);
  };

  startCamera = async () => {
    if (this.stream || this.state.starting || this.props.disabled) {
      return;
    }

    this.setState({ starting: true });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user" },
        audio: false,
      });
      this.stream = stream;

      if (this.videoRef.current) {
        this.videoRef.current.srcObject = stream;
      }

      this.setState({ starting: false });
    } catch (error) {
      this.setState({ starting: false });
      alertify.warning(I18n.t("profile_configuration_verification_camera_error"));
    }
  };

  startRecording = () => {
    if (!this.stream || this.state.recording || this.props.disabled) {
      return;
    }

    this.chunks = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9")
      ? "video/webm;codecs=vp9"
      : "video/webm";

    try {
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType });
    } catch (error) {
      alertify.warning(I18n.t("profile_configuration_verification_camera_error"));
      return;
    }

    this.mediaRecorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        this.chunks.push(event.data);
      }
    };

    this.mediaRecorder.onstop = () => {
      const blob = new Blob(this.chunks, { type: "video/webm" });
      const previewUrl = URL.createObjectURL(blob);
      const videoFile = new File([blob], `verification-face-${Date.now()}.webm`, {
        type: "video/webm",
      });

      this.setState({
        previewUrl,
        recording: false,
        seconds: 0,
      });
      this.props.onVideoChange(videoFile);
      this.stopStream();
    };

    this.mediaRecorder.start();
    this.setState({ recording: true, seconds: 0, previewUrl: "" });
    this.props.onVideoChange(null);

    this.timerId = setInterval(() => {
      this.setState((prevState) => {
        const nextSeconds = prevState.seconds + 1;

        if (nextSeconds >= MAX_RECORD_SECONDS) {
          this.stopRecording();
          return { seconds: MAX_RECORD_SECONDS };
        }

        return { seconds: nextSeconds };
      });
    }, 1000);
  };

  stopRecording = () => {
    this.clearTimer();

    if (this.mediaRecorder && this.mediaRecorder.state !== "inactive") {
      this.mediaRecorder.stop();
    }
  };

  onRerecord = () => {
    if (this.state.previewUrl) {
      URL.revokeObjectURL(this.state.previewUrl);
    }

    this.setState({
      previewUrl: "",
      seconds: 0,
      recording: false,
    });
    this.props.onVideoChange(null);
    this.startCamera();
  };

  render() {
    const { recording, seconds, previewUrl, starting } = this.state;
    const hasRecording = Boolean(previewUrl);

    return (
      <div className="profile-verification-recorder">
        <div className="profile-verification-recorder__stage">
          {hasRecording ? (
            <video
              src={previewUrl}
              className="profile-verification-recorder__video"
              controls
              playsInline
            />
          ) : (
            <video
              ref={this.videoRef}
              className="profile-verification-recorder__video"
              autoPlay
              muted
              playsInline
            />
          )}

          {!hasRecording && !starting && (
            <div className="profile-verification-recorder__overlay">
              <FaVideo />
              <span>
                <Translate value="profile_configuration_verification_face_hint" />
              </span>
            </div>
          )}
        </div>

        {recording && (
          <div className="profile-verification-recorder__timer">
            {String(seconds).padStart(2, "0")} / {MAX_RECORD_SECONDS}s
          </div>
        )}

        <div className="profile-verification-recorder__actions">
          {!hasRecording && !recording && (
            <button
              type="button"
              className="profile-config-btn"
              onClick={this.startRecording}
              disabled={starting || !this.stream || this.props.disabled}
            >
              <FaCircle />
              <Translate value="profile_configuration_verification_start_video" />
            </button>
          )}

          {recording && (
            <button
              type="button"
              className="profile-config-btn profile-config-btn--danger"
              onClick={this.stopRecording}
            >
              <FaStop />
              <Translate value="profile_configuration_verification_stop_video" />
            </button>
          )}

          {hasRecording && (
            <button
              type="button"
              className="profile-config-btn"
              onClick={this.onRerecord}
              disabled={this.props.disabled}
            >
              <FaRedo />
              <Translate value="profile_configuration_verification_rerecord" />
            </button>
          )}
        </div>
      </div>
    );
  }
}

export default UserVerificationFaceRecorder;
